import { memo, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { makeStyles, Typography } from '@material-ui/core';
import PeoplePageContainer from '../../shared/peoplePageContainer';
import UserTagsInfo from '../../shared/userTagsInfo';
import CustomButton from '../../shared/customButton';
import ManageUserTagsDialog from '../../shared/manageUserTagsDialog';
import CustomFormDrawer from '../../shared/customFormDrawer';
import ConditionalTooltip from '../../shared/conditionalTooltip';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import UserStatus from '../../shared/userStatus';
import AssignUsersDialog from '../../shared/assignUsersDialog';
import TransferEmployeesDialog from '../../shared/transferEmployeesDialog';
import EmploymentTerminationDateDialog from '../../shared/employmentTerminationDateDialog';
import AlertDialog from '../../shared/alertDialog';
import UserAvatar from '../../shared/userAvatar';
import RichTextPreview from '../../shared/richTextPreview';
import ActionButton from '../../shared/actionButton';
import {
  API_ORG_CHART,
  api_user,
  API_USER_FIELD_CONFIG,
  API_USERS_BASIC,
} from '../../../constants/apiRoutes';
import {
  PERMISSION_CODES,
  PERMISSION_SCOPES,
} from '../../../constants/permissions';
import { SYSTEM_ROLES } from '../../../constants/roles';
import { USER_STATUSES } from '../../../constants/statuses';
import {
  USER_EMPLOYMENT_INFORMATION,
  PEOPLE_DEFAULT_ORDERING,
  USER_BASIC_PERSONAL_INFORMATION,
  USER_PERSONAL_INFO_KEYS,
  USER_EMPLOYMENT_INFO_KEYS,
  LANGUAGES,
  GENDER_OPTIONS,
  SENSITIVE_DATA_DEFAULT,
} from '../../../constants/people';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { CURRENCIES } from '../../../constants/leveling';
import { sticky } from '../../../constants/helperCssRules';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { personSelector } from '../../../store/selectors/personSelector';
import {
  getEmploymentInfo,
  getPersonalInfo,
  updateEmploymentInfo,
  updatePersonalInfo,
  updateUserEmploymentInfo,
} from '../../../store/modules/person';
import { updateCurrentUserInfo } from '../../../store/modules/auth';
import {
  getAuthReporters,
  getAuthScopedUsers,
} from '../../../store/modules/accessControl';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import { clearJobTitles, getJobTitles } from '../../../store/modules/jobTitles';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import http from '../../../utility/http';
import {
  hasScopedAuthPermissionCheck,
  hasRequiredScopeCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  parseDuplicateParameters,
  showSuccessMessage,
  getPersonFullName,
  prepareTracksWithLevels,
  sortAlphabetically,
  prepareFrameworksWithTracks,
  prepareDivisionsWithDepartments,
} from '../../../utility/uiUtils';
import { formatDate } from '../../../utility/dateUtils';
import {
  isObjectEmpty,
  getItemById,
  isArrayEmpty,
  isItemInList,
} from '../../../utility/helpers';
import {
  prepareCategoriesWithTags,
  prepareUserTagCategories,
} from '../../../utility/tagUtils';
import {
  prepareUserStatuses,
  prepareInitialUserData,
  prepareTimezones,
  prepareEditPersonalInformationFields,
  prepareEditEmploymentInformationFields,
  prepareInitialEmploymentUserData,
  prepareHealthInsuranceValue,
  getFieldSettingsMap,
} from '../../../utility/people';
import { useTranslations } from '../../../utility/useTranslations';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  description: {
    paddingBottom: spacing(4),
    ...sticky(primary.white, 170),
  },
  statusWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: spacing(6),
  },
  informationWapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  employmentInfoWrapper: {
    marginTop: spacing(8),
  },
  editButton: {
    maxHeight: 32,
    padding: spacing(1, 4, 1, 4),
  },
  switchLabel: {
    fontSize: 14,
  },
  infoWrapper: {
    marginTop: spacing(8),
  },
  info: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
    gap: 16,
    position: 'relative',
  },
  jobInfo: {
    gap: 16,
    position: 'relative',
    display: 'grid',
  },
  smallerGap: {
    marginTop: spacing(4),
  },
  sectionTitle: {
    marginBottom: spacing(4),
  },
  sectionWrap: {
    backgroundColor: primary.bluish8,
    borderRadius: 8,
    height: 'fit-content',
    padding: spacing(3, 4, 3, 4),
    display: 'grid',
  },
  sectionField: {
    color: primary.bluish3,
  },
  userLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 14,
    lineHeight: '20px',
  },
  disabledUserAction: {
    cursor: 'not-allowed',
  },
  currency: {
    marginRight: spacing(2.75),
  },
  block: {
    display: 'flex',
    gap: 12,
  },
  editDateButton: {
    width: 12,
    height: 12,
  },
}));

const {
  REPORT_TO,
  FRAMEWORK,
  TRACK,
  LEVEL,
  DIVISION,
  DEPARTMENT,
  EMPLOYMENT_DATE,
  EMPLOYMENT_TERMINATION_DATE,
  PRIVATE_HEALTH_INSURANCE,
  JOB_TITLE,
  WORKING_HOURS,
  NET_SALARY,
  GROSS_SALARY_1,
  GROSS_SALARY_2,
} = USER_EMPLOYMENT_INFORMATION;

const {
  FIRST_NAME,
  LAST_NAME,
  STATUS,
  GENDER,
  LANGUAGE,
  TIME_ZONE,
  DATE_OF_BIRTH,
  EXPERIENCE,
  YEARS_OF_EXPERIENCE,
  SOCIAL_ACCOUNTS,
  EXTERNAL_RESOURCE,
} = USER_BASIC_PERSONAL_INFORMATION;

const PeopleInfoPage = ({
  location,
  auth,
  accessControl,
  organizationSettings,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const { id } = useParams();
  const userId = (!Number.isNaN(Number(id)) && +id) || -1;
  const translations = useTranslations(APP_PAGES.PEOPLE_INFO);

  const user = useSelector(personSelector);
  const { jobTitles } = useSelector(jobTitlesSelector);
  const { tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const { frameworks } = useSelector(levelsAndTracksSelector);
  const { categories } = useSelector(tagsSelector);

  const [isLoading, setIsLoading] = useState(true);
  const [mentor, setMentor] = useState(null);
  const [organizationMentor, setOrganizationMentor] = useState(null);
  const [
    isEditPersonalInformationDialogOpened,
    setIsEditPersonalInformationDialogOpened,
  ] = useState(false);
  const [
    isEditEmploymentInformationDialogOpened,
    setIsEditEmploymentInformationDialogOpened,
  ] = useState(false);
  const [isAddUserTagsDialogOpened, setIsAddUserTagsDialogOpened] =
    useState(false);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [fieldsSettings, setFieldsSettings] = useState([]);
  const [isInviteDialogOpened, setIsInviteDialogOpened] = useState(false);
  const [isReinviteDialogOpened, setIsReinviteDialogOpened] = useState(false);
  const [isRevokeAccessDialogOpened, setIsRevokeAccessDialogOpened] =
    useState(false);
  const [isReactivateDialogOpened, setIsReactivateDialogOpened] =
    useState(false);
  const [isDeactivateDialogOpened, setIsDeactivateDialogOpened] =
    useState(false);
  const [isDeleteUserDialogOpened, setIsDeleteUserDialogOpened] =
    useState(false);
  const [isTransferUsersDialogOpened, setIsTransferUsersDialogOpened] =
    useState(false);
  const [usersToTransfer, setUsersToTransfer] = useState([]);

  const { directReportsSet, indirectReportsSet, customEmployeesScopeSet } =
    accessControl;
  const isCurrentUserProfile = auth.id === userId;
  const canEdit = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_EDIT,
    accessControl,
    auth.id,
    userId
  );
  const canDelete = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_DELETE,
    accessControl,
    auth.id,
    userId
  );
  const canViewSensitiveData = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_SENSITIVE_DATA_VIEW,
    accessControl,
    auth.id,
    userId
  );
  const hasGlobalEditScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PROFILES_EDIT,
    PERMISSION_SCOPES.GLOBAL,
    accessControl
  );
  const hasCustomEditScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PROFILES_EDIT,
    PERMISSION_SCOPES.CUSTOM,
    accessControl
  );
  const isUserDeactivated = user[STATUS] === USER_STATUSES.DEACTIVATED;
  const userStatuses = prepareUserStatuses(translations.userStatuses.statuses);
  const userStatus = getItemById(userStatuses, user?.[STATUS]);
  const userTags = sortAlphabetically(user?.tags || []);
  const userTrack = tracks?.find(track => track.id === user.track);
  const userFramework = getItemById(frameworks, userTrack?.framework);
  const userTagCategories = prepareUserTagCategories(categories, userTags);
  const userCurrency =
    getItemById(CURRENCIES, userFramework?.currency, 'key')?.value || '';
  const timezones = prepareTimezones();
  const categoriesWithTags = prepareCategoriesWithTags(categories);
  const preparedPersonalInfoUserData = prepareInitialUserData(
    user,
    canViewSensitiveData,
    fieldsSettings
  );

  const onRedirectToPeoplePage = (
    directReports,
    indirectReports,
    customEmployeesScope
  ) => {
    if (
      (!hasCustomEditScope &&
        !isItemInList(directReports, userId) &&
        !isItemInList(indirectReports, userId)) ||
      (hasCustomEditScope && !isItemInList(customEmployeesScope, userId))
    ) {
      return navigate('/people');
    }
  };

  const canAccessUserProfileCheck = curentUserId =>
    isUserProfileAccessibleCheck(accessControl, auth.id, curentUserId);

  const getAvailableManagers = () =>
    http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
        [PARAMS.AVAILABLE_MANAGERS]: userId,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    });

  const getOrganizationMentor = () =>
    http.get(API_ORG_CHART).then(({ data }) => {
      return {
        ...data,
        last_name: translations.organizationUser,
        isCeo: true,
      };
    });

  const getMentor = (
    mentorId,
    reportToOrganization,
    orgMentorOverride,
    isDeactivated = isUserDeactivated
  ) => {
    const orgMentor = orgMentorOverride ?? organizationMentor;

    if (isDeactivated) {
      setMentor(null);

      return Promise.resolve();
    }

    if (!mentorId && !reportToOrganization) {
      setMentor(null);

      return Promise.resolve();
    }

    if (reportToOrganization || mentorId === orgMentor?.id) {
      setMentor(orgMentor);

      return Promise.resolve();
    }

    return http
      .get(API_USERS_BASIC, {
        params: {
          ...PEOPLE_DEFAULT_ORDERING,
          id: mentorId,
        },
        paramsSerializer: d => parseDuplicateParameters(d),
      })
      .then(({ data }) => {
        setMentor(data?.[0] || null);
      });
  };

  const checkUsersDirectReports = async personId => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [REPORT_TO]: personId,
      },
    });

    return data;
  };

  const getInitialData = async () => {
    await Promise.all([
      getTagsCategories(dispatch),
      getJobTitles(dispatch),
      getLevelsAndTracks(dispatch),
      getDivisionsAndDepartments(dispatch),
    ]).then(async () => {
      await getPersonalInfo(dispatch, userId);
      const { data } = await http.get(API_USER_FIELD_CONFIG);
      const empInfo = await getEmploymentInfo(dispatch, userId);
      const orgMentor = await getOrganizationMentor();
      const [managers] = await Promise.all([
        getAvailableManagers(),
        getMentor(
          empInfo[REPORT_TO],
          empInfo.report_to_organization,
          orgMentor,
          empInfo[STATUS] === USER_STATUSES.DEACTIVATED
        ),
      ]);

      setIsLoading(false);
      setFieldsSettings(getFieldSettingsMap(data));
      setOrganizationMentor(orgMentor);
      setAvailableManagers([orgMentor].concat(managers.data));
    });
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = useCallback(() => {
    dispatch(clearTagsCategories());
    dispatch(clearJobTitles());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
  }, [dispatch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const toggleTransferUsersDialog = (users = []) => {
    setIsTransferUsersDialogOpened(prevIsOpened => !prevIsOpened);
    setUsersToTransfer(users);
  };

  const handleToggleInvite = () =>
    setIsInviteDialogOpened(prevIsOpened => !prevIsOpened);

  const onInvite = async () => {
    try {
      await updateEmploymentInfo(dispatch, userId, {
        [STATUS]: USER_STATUSES.ACTIVE_INVITED,
      });
      showSuccessMessage(translations.inviteUserDialog.inviteSuccess);
    } finally {
      handleToggleInvite();
    }
  };

  const handleToggleReinvite = () =>
    setIsReinviteDialogOpened(prevIsOpened => !prevIsOpened);

  const onReinvite = async () => {
    try {
      await updateEmploymentInfo(dispatch, userId, {
        [STATUS]: USER_STATUSES.ACTIVE_INVITED,
      });
      showSuccessMessage(translations.reinviteUserDialog.reinviteSuccess);
    } finally {
      handleToggleReinvite();
    }
  };

  const handleToggleRevokeAccess = () =>
    setIsRevokeAccessDialogOpened(prevIsOpened => !prevIsOpened);

  const onRevokeAccess = async () => {
    try {
      await updateEmploymentInfo(dispatch, userId, {
        [STATUS]: USER_STATUSES.ACTIVE_WITHOUT_ACCESS,
      });
      showSuccessMessage(translations.revokeAccessUserDialog.revokeSuccess);
    } finally {
      handleToggleRevokeAccess();
    }
  };

  const handleToggleDeactivate = () =>
    setIsDeactivateDialogOpened(prevIsOpened => !prevIsOpened);

  const handleOpenDeactivate = async () => {
    const reporters = await checkUsersDirectReports(userId);

    if (!isArrayEmpty(reporters)) {
      return toggleTransferUsersDialog(reporters);
    }

    handleToggleDeactivate();
  };

  const onDeactivate = async value => {
    const userData = {
      [STATUS]: USER_STATUSES.DEACTIVATED,
      [EMPLOYMENT_TERMINATION_DATE]: value,
    };

    try {
      await updateEmploymentInfo(dispatch, userId, userData);
      setMentor(null);
      showSuccessMessage(
        translations.employmentTerminationDateDialog.successMessage
      );
    } finally {
      let reporters = {};
      let customEmployeesScope = [];

      if (!isCurrentUserProfile && !hasGlobalEditScope) {
        if (directReportsSet.has(user.id) || indirectReportsSet.has(user.id)) {
          reporters = await getAuthReporters(dispatch);
        }
        if (customEmployeesScopeSet.has(user.id)) {
          customEmployeesScope = await getAuthScopedUsers(dispatch);
        }
        onRedirectToPeoplePage(
          reporters.directReports,
          reporters.indirectReports,
          customEmployeesScope
        );
      }
    }
  };

  const handleUpdateTerminationDate = async value => {
    const userData = {
      [EMPLOYMENT_TERMINATION_DATE]: value,
    };

    const updateResponse = await updateEmploymentInfo(
      dispatch,
      userId,
      userData
    );
    showSuccessMessage(translations.successUpdate);
    return updateResponse;
  };

  const onUpdateTerminationDate = value =>
    isUserDeactivated
      ? handleUpdateTerminationDate(value)
      : onDeactivate(value);

  const onTransferUsers = async (terminationDate, personsToTransferId) => {
    let reporters = {};
    let customEmployeesScope = [];
    await updateEmploymentInfo(dispatch, userId, {
      [STATUS]: USER_STATUSES.DEACTIVATED,
      [EMPLOYMENT_TERMINATION_DATE]: terminationDate,
    });
    setMentor(null);
    showSuccessMessage(translations.transferEmployeesDialog.successMessage);
    if (!isCurrentUserProfile && !hasGlobalEditScope) {
      if (
        personsToTransferId === auth.id ||
        directReportsSet.has(user.id) ||
        indirectReportsSet.has(user.id)
      ) {
        reporters = await getAuthReporters(dispatch);
      }
      if (customEmployeesScopeSet.has(user.id)) {
        customEmployeesScope = getAuthScopedUsers(dispatch);
      }
      onRedirectToPeoplePage(
        reporters.directReports,
        reporters.indirectReports,
        customEmployeesScope
      );
    }
  };

  const handleToggleReactivate = () =>
    setIsReactivateDialogOpened(prevIsOpened => !prevIsOpened);

  const onReactivate = async reportTo => {
    try {
      const { data } = await updateEmploymentInfo(dispatch, userId, {
        [STATUS]: USER_STATUSES.ACTIVE_WITHOUT_ACCESS,
        [EMPLOYMENT_TERMINATION_DATE]: null,
        [REPORT_TO]: reportTo,
      });

      await getMentor(
        data[REPORT_TO],
        data.report_to_organization,
        undefined,
        false
      );
      showSuccessMessage(translations.reactivateUserDialog.reactivateSuccess);
    } finally {
      handleToggleReactivate();
    }
  };

  const toggleDeleteDialog = () =>
    setIsDeleteUserDialogOpened(prevIsOpened => !prevIsOpened);

  const onDeleteUser = () => {
    return http.delete(api_user(user.id)).then(() => {
      showSuccessMessage(translations.deleteUserDialog.deleteSuccess);
      navigate('/people');
    });
  };

  const prepareSavableFieldKeys = baseKeys =>
    canViewSensitiveData
      ? baseKeys
      : baseKeys.filter(key => !fieldsSettings[key]?.is_sensitive);

  const onUpdatePersonalInfo = async values => {
    const userData = {};
    const keys = prepareSavableFieldKeys(USER_PERSONAL_INFO_KEYS);
    keys.forEach(key => {
      if (user[key] !== values[key]) {
        if (key === GENDER) {
          if (user[key] !== values[key]?.name) {
            userData[key] = values[key]?.name;
          }
          return;
        }
        if (key === YEARS_OF_EXPERIENCE && !values[key]) {
          userData[key] = null;
          return;
        }
        if (key === LANGUAGE) {
          if (user[key] !== values[key]?.value) {
            userData[key] = values[key]?.value;
          }
          return;
        }
        if (key === TIME_ZONE) {
          if (user[key] !== values[key]?.value) {
            userData[key] = values[key]?.value;
          }
          return;
        }
        userData[key] = values[key];
      }
    });

    if (!isObjectEmpty(userData)) {
      await updatePersonalInfo(dispatch, userId, userData);
      showSuccessMessage(translations.successUpdate);

      if (
        isCurrentUserProfile &&
        (userData[FIRST_NAME] !== user[FIRST_NAME] ||
          userData[LAST_NAME] !== user[LAST_NAME])
      ) {
        dispatch(
          updateCurrentUserInfo({
            ...(userData[FIRST_NAME]
              ? {
                  [FIRST_NAME]: userData[FIRST_NAME],
                }
              : {}),
            ...(userData[LAST_NAME]
              ? {
                  [LAST_NAME]:
                    userData[USER_BASIC_PERSONAL_INFORMATION.LAST_NAME],
                }
              : {}),
          })
        );
      }
    }

    return Promise.resolve();
  };

  const onUpdateEmploymentInfo = async values => {
    const userData = {};
    const keys = prepareSavableFieldKeys(USER_EMPLOYMENT_INFO_KEYS);
    keys.forEach(key => {
      if (user[key] !== values[key]) {
        if (key === WORKING_HOURS && !values[key]) {
          userData[key] = null;
          return;
        }
        if ([GROSS_SALARY_1, GROSS_SALARY_2, NET_SALARY].includes(key)) {
          if (!values[key]) {
            userData[key] = null;
            return;
          }
        }
        if (key === JOB_TITLE) {
          if (user[key]?.id !== values[key]) {
            userData[key] = values[key];
          }
          return;
        }
        userData[key] = values[key];
      }
    });

    if (!isObjectEmpty(userData)) {
      const { data: updatedEmploymentInfo } = await updateUserEmploymentInfo(
        dispatch,
        userId,
        userData
      );

      if (userData[REPORT_TO] !== undefined) {
        await getMentor(
          updatedEmploymentInfo[REPORT_TO],
          updatedEmploymentInfo.report_to_organization
        );
      }

      showSuccessMessage(translations.successUpdate);
      let reporters = {};
      let customEmployeesScope = [];

      if (isCurrentUserProfile && userData[JOB_TITLE]) {
        dispatch(
          updateCurrentUserInfo({
            [JOB_TITLE]: getItemById(jobTitles, userData[JOB_TITLE]),
          })
        );
      }

      if (!isCurrentUserProfile && !hasGlobalEditScope) {
        if (userData[REPORT_TO]) {
          if (
            directReportsSet.has(user.id) ||
            indirectReportsSet.has(user.id) ||
            userData[REPORT_TO] === auth.id
          ) {
            reporters = await getAuthReporters(dispatch);
          }
        }
        if (
          (customEmployeesScopeSet.has(user.id) ||
            directReportsSet.has(user.id) ||
            indirectReportsSet.has(user.id)) &&
          (userData[DIVISION] ||
            userData[DEPARTMENT] ||
            userData[FRAMEWORK] ||
            userData[TRACK] ||
            userData[LEVEL] ||
            userData[JOB_TITLE])
        ) {
          customEmployeesScope = await getAuthScopedUsers(dispatch);
        }

        onRedirectToPeoplePage(
          reporters.directReports,
          reporters.indirectReports,
          customEmployeesScope
        );
      }
    }

    return Promise.resolve();
  };

  const handleCloseAddUserTagsDialog = () =>
    setIsAddUserTagsDialogOpened(false);

  const onSaveTags = updatedTags => {
    setIsAddUserTagsDialogOpened(false);
    return updateEmploymentInfo(dispatch, userId, updatedTags).then(
      async () => {
        showSuccessMessage(translations.manageUserTagsDialog.successMessage);
        if (hasCustomEditScope) {
          const customEmployeesScope = await getAuthScopedUsers(dispatch);

          onRedirectToPeoplePage([], [], customEmployeesScope);
        }
      }
    );
  };

  const handleMentorClick = () => navigate(`/people/${mentor.id}`);

  const onAddTagsButton = () => setIsAddUserTagsDialogOpened(true);

  const handleCloseEditPersonalInformationSidebar = () =>
    setIsEditPersonalInformationDialogOpened(false);

  const handleCloseEditEmploymentInformationSidebar = () =>
    setIsEditEmploymentInformationDialogOpened(false);

  const getSensitiveAwareReadValue = (fieldName, value) =>
    fieldsSettings[fieldName]?.is_sensitive && !canViewSensitiveData
      ? SENSITIVE_DATA_DEFAULT
      : value;

  const getReadModeEmploymentData = () => {
    const userDepartment = departments.find(
      department => department.id === user.department
    );
    const userDivision = divisions.find(
      division => division.id === userDepartment?.division
    );
    const netSalary = user[NET_SALARY]
      ? `${userCurrency} ${user[NET_SALARY]}`
      : '-';
    const gross1 = user[GROSS_SALARY_1]
      ? `${userCurrency} ${user[GROSS_SALARY_1]}`
      : '-';
    const gross2 = user[GROSS_SALARY_2]
      ? `${userCurrency} ${user[GROSS_SALARY_2]}`
      : '-';

    return {
      [JOB_TITLE]: getSensitiveAwareReadValue(
        JOB_TITLE,
        jobTitles.find(jTitle => jTitle.id === user?.position?.id)?.name
      ),
      [FRAMEWORK]: getSensitiveAwareReadValue(FRAMEWORK, userFramework?.name),
      [TRACK]: getSensitiveAwareReadValue(TRACK, userTrack?.name),
      [LEVEL]: getSensitiveAwareReadValue(
        LEVEL,
        userTrack?.levels.find(trLvl => trLvl?.id === user.level)?.name
      ),
      [DIVISION]: getSensitiveAwareReadValue(DIVISION, userDivision?.name),
      [DEPARTMENT]: getSensitiveAwareReadValue(
        DEPARTMENT,
        userDepartment?.name
      ),
      [REPORT_TO]: getSensitiveAwareReadValue(REPORT_TO, mentor),
      userName: getPersonFullName(user),
      [EMPLOYMENT_DATE]: getSensitiveAwareReadValue(
        EMPLOYMENT_DATE,
        user[EMPLOYMENT_DATE] || null
      ),
      [EMPLOYMENT_TERMINATION_DATE]: getSensitiveAwareReadValue(
        EMPLOYMENT_TERMINATION_DATE,
        user[EMPLOYMENT_TERMINATION_DATE] || null
      ),
      [PRIVATE_HEALTH_INSURANCE]: getSensitiveAwareReadValue(
        PRIVATE_HEALTH_INSURANCE,
        user[PRIVATE_HEALTH_INSURANCE]
      ),
      [WORKING_HOURS]: getSensitiveAwareReadValue(
        WORKING_HOURS,
        user[WORKING_HOURS]?.toString()
      ),
      [NET_SALARY]: getSensitiveAwareReadValue(NET_SALARY, netSalary),
      [GROSS_SALARY_1]: getSensitiveAwareReadValue(GROSS_SALARY_1, gross1),
      [GROSS_SALARY_2]: getSensitiveAwareReadValue(GROSS_SALARY_2, gross2),
    };
  };

  const preparedEmploymentInfoUserData = getReadModeEmploymentData();

  const renderData = (sectionData, preparedUserData) => {
    return Object.keys(sectionData.fields).map(fieldKey => {
      const isReportToField = fieldKey === REPORT_TO;
      const isHealthInsuranceField = fieldKey === PRIVATE_HEALTH_INSURANCE;
      const isRichText = [
        EXPERIENCE,
        SOCIAL_ACCOUNTS,
        EXTERNAL_RESOURCE,
      ].includes(fieldKey);
      const hasEdit =
        fieldKey === EMPLOYMENT_TERMINATION_DATE && isUserDeactivated;

      let value = preparedUserData[fieldKey];
      const isDateField =
        (fieldKey === DATE_OF_BIRTH && preparedUserData[DATE_OF_BIRTH]) ||
        (fieldKey === EMPLOYMENT_DATE && preparedUserData[EMPLOYMENT_DATE]) ||
        (fieldKey === EMPLOYMENT_TERMINATION_DATE &&
          preparedUserData[EMPLOYMENT_TERMINATION_DATE]);
      value =
        isDateField && value && value !== SENSITIVE_DATA_DEFAULT
          ? formatDate(new Date(value))
          : value;
      value =
        isHealthInsuranceField && value !== SENSITIVE_DATA_DEFAULT
          ? prepareHealthInsuranceValue(value)
          : value;
      value =
        [LANGUAGE, GENDER, TIME_ZONE].includes(fieldKey) &&
        value !== SENSITIVE_DATA_DEFAULT
          ? value?.label || value?.name
          : value;

      return (
        fieldsSettings[fieldKey]?.is_visible && (
          <div key={fieldKey} className={classes.sectionWrap}>
            <div className={classes.block}>
              <Typography variant="body2" className={classes.sectionField}>
                {sectionData.fields[fieldKey]}
              </Typography>
              {hasEdit && (
                <ActionButton
                  type={ACTION_BUTTON_TYPES.EDIT}
                  iconButtonClass={classes.editDateButton}
                  onClickHandler={handleToggleDeactivate}
                  tooltipText={translations.edit}
                />
              )}
            </div>

            {isReportToField && value !== SENSITIVE_DATA_DEFAULT ? (
              <>
                {value ? (
                  <UserAvatar
                    labelClass={classes.userLabel}
                    user={value}
                    isOrganizationUser={organizationMentor?.id === value.id}
                    clickableCaption={
                      preparedUserData?.[REPORT_TO]?.id !==
                        organizationMentor?.id &&
                      canAccessUserProfileCheck(preparedUserData[REPORT_TO])
                    }
                    onClickHandler={handleMentorClick}
                    extraSmall
                    caption
                  />
                ) : (
                  <TextBoxWithTooltip variant="body1" text="-" />
                )}
              </>
            ) : (
              <>
                {isRichText ? (
                  <RichTextPreview text={value || '-'} />
                ) : (
                  <TextBoxWithTooltip variant="body1" text={value || '-'} />
                )}
              </>
            )}
          </div>
        )
      );
    });
  };

  const hasVisibleFieldsInSection = sectionData =>
    Object.keys(sectionData.fields).some(
      fieldKey => fieldsSettings[fieldKey]?.is_visible
    );

  const renderSection = (
    sectionData,
    preparedUserData,
    isFullWidth = false
  ) => {
    return (
      hasVisibleFieldsInSection(sectionData) && (
        <div
          className={classNames(classes.infoWrapper, {
            [classes.smallerGap]: !sectionData.title,
          })}
        >
          {sectionData.title && (
            <Typography variant="h5" className={classes.sectionTitle}>
              {sectionData.title}
            </Typography>
          )}
          <div
            className={classNames({
              [classes.info]: !isFullWidth,
              [classes.jobInfo]: isFullWidth,
            })}
          >
            {renderData(sectionData, preparedUserData)}
          </div>
        </div>
      )
    );
  };

  return (
    <PeoplePageContainer
      {...rest}
      auth={auth}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      navigate={navigate}
      dispatch={dispatch}
      translations={translations}
      location={location}
      userId={userId}
      shouldPassProps={false}
    >
      {!isLoading && (
        <div>
          <Typography className={classes.description} variant="body1">
            {isCurrentUserProfile
              ? translations.descriptionOwnProfile
              : translations.description}
          </Typography>
          <div className={classes.statusWrapper}>
            <Typography variant="h3" className={classes.sectionTitle}>
              {translations.statusLabel}
            </Typography>
            <UserStatus
              translations={translations.userStatuses.actions}
              status={userStatus}
              hasActions={!isCurrentUserProfile && (canEdit || canDelete)}
              canEdit={canEdit}
              canDelete={canDelete}
              onInvite={handleToggleInvite}
              onReinvite={handleToggleReinvite}
              onRevokeAccess={handleToggleRevokeAccess}
              onDeactivate={handleOpenDeactivate}
              onReactivate={handleToggleReactivate}
              onDelete={toggleDeleteDialog}
            />
          </div>
          <div className={classes.informationWapper}>
            <Typography variant="h3">
              {translations.personalInformationLabel}
            </Typography>
            {canEdit && (
              <ConditionalTooltip
                className={classes.disabledUserAction}
                message={translations.disabledEditActionTooltip}
                addTooltip={isUserDeactivated}
              >
                <CustomButton
                  className={classes.editButton}
                  type="withTextLightRounded"
                  disabled={isUserDeactivated && canEdit}
                  onClick={() => setIsEditPersonalInformationDialogOpened(true)}
                >
                  {translations.edit}
                </CustomButton>
              </ConditionalTooltip>
            )}
          </div>
          {renderSection(
            translations.basicPersonalInformation,
            preparedPersonalInfoUserData
          )}
          {renderSection(
            translations.personalDetails,
            preparedPersonalInfoUserData
          )}
          {renderSection(
            translations.preferences,
            preparedPersonalInfoUserData
          )}
          {renderSection(
            translations.identification,
            preparedPersonalInfoUserData
          )}
          {renderSection(
            translations.professionalBackground,
            preparedPersonalInfoUserData
          )}
          {renderSection(
            translations.previousWorkExperience,
            preparedPersonalInfoUserData,
            true
          )}
          <div className={classes.jobInfo}>
            {renderSection(
              translations.additionalPersonalInformation,
              preparedPersonalInfoUserData,
              true
            )}
          </div>
          <div
            className={classNames(
              classes.informationWapper,
              classes.employmentInfoWrapper
            )}
          >
            <Typography variant="h3">{translations.employmentInfo}</Typography>
            {canEdit && (
              <ConditionalTooltip
                className={classes.disabledUserAction}
                message={translations.disabledEditActionTooltip}
                addTooltip={isUserDeactivated}
              >
                <CustomButton
                  className={classes.editButton}
                  type="withTextLightRounded"
                  disabled={isUserDeactivated && canEdit}
                  onClick={() =>
                    setIsEditEmploymentInformationDialogOpened(true)
                  }
                >
                  {translations.edit}
                </CustomButton>
              </ConditionalTooltip>
            )}
          </div>
          {renderSection(
            translations.organization,
            preparedEmploymentInfoUserData
          )}
          {renderSection(
            translations.employmentDetails,
            preparedEmploymentInfoUserData
          )}
          {renderSection(
            translations.compensation,
            preparedEmploymentInfoUserData
          )}
          {renderSection(
            translations.additionalEmploymentInformation,
            preparedEmploymentInfoUserData
          )}
          <UserTagsInfo
            translations={translations}
            categories={userTagCategories}
            canAddTags={canEdit}
            isUserDeactivated={isUserDeactivated}
            onAddButton={onAddTagsButton}
          />
          <ManageUserTagsDialog
            translations={translations.manageUserTagsDialog}
            selectedTags={userTags}
            categories={categoriesWithTags}
            isOpened={isAddUserTagsDialogOpened}
            onCancel={handleCloseAddUserTagsDialog}
            onSave={onSaveTags}
          />
          {canEdit && (
            <CustomFormDrawer
              translations={translations}
              titleText={translations.editPersonalInformationTitle}
              initialData={preparedPersonalInfoUserData}
              isOpened={isEditPersonalInformationDialogOpened}
              fields={prepareEditPersonalInformationFields(
                user,
                canViewSensitiveData,
                fieldsSettings
              )}
              genders={GENDER_OPTIONS}
              languages={LANGUAGES}
              timezones={timezones}
              onClose={handleCloseEditPersonalInformationSidebar}
              onSave={onUpdatePersonalInfo}
              hasCancelButton
              isInitialValid
              hideDelete
            />
          )}
          {canEdit && (
            <CustomFormDrawer
              translations={translations}
              titleText={translations.editEmploymentInformationTitle}
              initialData={prepareInitialEmploymentUserData(
                user,
                canViewSensitiveData,
                tracks,
                frameworks,
                fieldsSettings,
                mentor
              )}
              isOpened={isEditEmploymentInformationDialogOpened}
              fields={prepareEditEmploymentInformationFields(
                classes,
                canViewSensitiveData,
                fieldsSettings
              )}
              positions={jobTitles}
              tracks={prepareTracksWithLevels(tracks)}
              frameworks={prepareFrameworksWithTracks(frameworks)}
              divisions={prepareDivisionsWithDepartments(divisions)}
              departments={departments}
              availableManagers={availableManagers}
              onClose={handleCloseEditEmploymentInformationSidebar}
              onSave={onUpdateEmploymentInfo}
              hasCancelButton
              isInitialValid
              hideDelete
            />
          )}
          {isUserDeactivated && (
            <AssignUsersDialog
              translations={translations.reactivateUserDialog}
              isOpened={isReactivateDialogOpened}
              allUsers={availableManagers}
              onCancel={handleToggleReactivate}
              onSave={onReactivate}
              shouldResetSelection
            />
          )}
          <TransferEmployeesDialog
            translations={translations.transferEmployeesDialog}
            isOpened={isTransferUsersDialogOpened}
            currentUserId={userId}
            minDate={user[EMPLOYMENT_DATE]}
            users={usersToTransfer}
            organizationUser={organizationMentor}
            onClose={toggleTransferUsersDialog}
            onConfirm={onTransferUsers}
          />
          <EmploymentTerminationDateDialog
            translations={translations.employmentTerminationDateDialog}
            isOpened={isDeactivateDialogOpened}
            initValue={user[EMPLOYMENT_TERMINATION_DATE]}
            minDate={user[EMPLOYMENT_DATE]}
            onClose={handleToggleDeactivate}
            onConfirm={onUpdateTerminationDate}
            isUserDeactivated
          />
          <AlertDialog
            translations={translations.inviteUserDialog}
            isOpened={isInviteDialogOpened}
            onClose={handleToggleInvite}
            onConfirm={onInvite}
          />
          <AlertDialog
            translations={translations.reinviteUserDialog}
            isOpened={isReinviteDialogOpened}
            onClose={handleToggleReinvite}
            onConfirm={onReinvite}
          />
          <AlertDialog
            translations={translations.revokeAccessUserDialog}
            isOpened={isRevokeAccessDialogOpened}
            onClose={handleToggleRevokeAccess}
            onConfirm={onRevokeAccess}
          />
          <AlertDialog
            translations={translations.deleteUserDialog}
            isOpened={isDeleteUserDialogOpened}
            onClose={toggleDeleteDialog}
            onConfirm={onDeleteUser}
            isWarning
          />
        </div>
      )}
    </PeoplePageContainer>
  );
};

export default memo(PeopleInfoPage);
